/**
 * Wave 4 stage K (module analytics): the wire format of the self-hosted, anonymous player
 * analytics, shared by the client event layer (src/client/analytics) and the server ingestion
 * (src/server/analytics). See docs/TECHNICAL.md#analytics-service.
 *
 * Privacy rules enforced here (and again on the server):
 * - no names, e-mail addresses, IP addresses, referrers or free text other than a short,
 *   sanitised client error message;
 * - without consent a batch carries no id at all and only the anonymous count kinds
 *   (`session`, `funnel`, `gameStart`) are kept, as day counters;
 * - with consent a random anonymous player id (`pid`), kept in the browser until consent is
 *   withdrawn (withdrawing also deletes the rows of that id on the server).
 *
 * `validateBatch` builds every clean event field by field from a whitelist, so nothing that is
 * not listed below can ever reach the database.
 */
import { parseAbExperimentId } from "./ads";

export const ANALYTICS_VERSION = 1;

export const ANALYTICS_LIMITS = {
  /** Request body limit of one batch. */
  maxBodyBytes: 16 * 1024,
  /** Events in one batch. */
  maxEvents: 60,
  /** Short id strings (map, feature, level, objective ids). */
  maxIdLength: 48,
  /** Client error messages (sanitised, cut). */
  maxErrorLength: 160,
  /** Count of one feature in one event. */
  maxFeatureCount: 1000,
  /** Game length in minutes. */
  maxGameMinutes: 600,
} as const;

export const SOURCES = ["portal", "site", "discord", "link"] as const;
export type AnalyticsSource = (typeof SOURCES)[number];

export const DEVICES = ["desktop", "mobile", "tablet"] as const;
export type AnalyticsDevice = (typeof DEVICES)[number];

export const BROWSERS = ["chrome", "firefox", "safari", "edge", "other"] as const;
export type AnalyticsBrowser = (typeof BROWSERS)[number];

/** The funnel, in order: each step is sent at most once per page session. */
export const FUNNEL_STEPS = ["visit", "menu", "setup", "spawn", "firstAttack", "firstMinute", "gameEnd", "secondGame"] as const;
export type FunnelStep = (typeof FUNNEL_STEPS)[number];

export const GAME_KINDS = ["sp", "mp"] as const;
export type GameKind = (typeof GAME_KINDS)[number];

export const RULESETS = ["simple", "full", "custom"] as const;
export const DIFFICULTIES = ["easy", "medium", "hard", "impossible", "none"] as const;
export const GAME_RESULTS = ["win", "loss", "left"] as const;
export type GameResultKind = (typeof GAME_RESULTS)[number];
/** `closed`: the tab was closed or reloaded during the game (no Leave button, no session end). */
export const END_REASONS = ["won", "otherWon", "eliminated", "left", "disconnected", "error", "closed"] as const;
export type EndReason = (typeof END_REASONS)[number];

export const CAMPAIGN_ACTIONS = ["start", "finish", "fail", "hint"] as const;
/** Wave 4 stage L (ads): the arms of an A/B experiment ("off" = without the tested placement). */
export const AB_ARMS = ["on", "off"] as const;
export const ERROR_SOURCES = ["window", "promise", "worker"] as const;

/** Event kinds kept without consent (as anonymous day counters, never with an id). */
export const ANONYMOUS_KINDS: ReadonlySet<string> = new Set(["session", "funnel", "gameStart"]);

export type AnalyticsEvent =
  | { e: "session"; src: AnalyticsSource; dev: AnalyticsDevice; br: AnalyticsBrowser; lang: string }
  | { e: "funnel"; step: FunnelStep }
  | { e: "gameStart"; kind: GameKind; rs: string; mode: string; map: string; diff: string }
  | {
      e: "gameEnd";
      kind: GameKind;
      rs: string;
      mode: string;
      map: string;
      diff: string;
      result: GameResultKind;
      reason: EndReason;
      /** Game length in minutes (game time, after the spawn phase). */
      min: number;
      /** Final land share in whole percent. */
      share: number;
    }
  | { e: "feature"; f: string; n: number }
  | { e: "campaign"; lvl: string; act: (typeof CAMPAIGN_ACTIONS)[number]; obj?: string }
  | { e: "tech"; dev: AnalyticsDevice; br: AnalyticsBrowser; load?: number; fps?: number; tick?: number }
  | { e: "error"; dev: AnalyticsDevice; br: AnalyticsBrowser; where: (typeof ERROR_SOURCES)[number]; msg: string }
  // Wave 4 stage L (ads): the A/B arm of an experiment (e.g. `ads.banner.r1`), consent only;
  // the first arm per player and experiment is kept for the retention comparison.
  | { e: "ab"; x: string; arm: (typeof AB_ARMS)[number] };

export type AnalyticsEventKind = AnalyticsEvent["e"];

export interface AnalyticsBatch {
  v: typeof ANALYTICS_VERSION;
  consent: boolean;
  /** Random anonymous player id (32 hex), only with consent. */
  pid?: string;
  events: AnalyticsEvent[];
}

export type BatchValidation =
  | { ok: true; batch: AnalyticsBatch; dropped: number }
  | { ok: false; error: "invalid_batch" | "unsupported_version" | "id_without_consent" | "missing_id" | "too_many_events" };

export const ANON_ID_RE = /^[0-9a-f]{32}$/;
const ID_RE = /^[A-Za-z0-9_.:+-]{1,48}$/;

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function oneOf<T extends string>(list: readonly T[], v: unknown): T | null {
  return typeof v === "string" && (list as readonly string[]).includes(v) ? (v as T) : null;
}

function idStr(v: unknown): string | null {
  return typeof v === "string" && ID_RE.test(v) ? v : null;
}

function intIn(v: unknown, min: number, max: number): number | null {
  if (typeof v !== "number" || !Number.isFinite(v)) return null;
  const n = Math.round(v);
  return n < min || n > max ? null : n;
}

/** Strip anything that could identify a person from an error message and cut it short. */
export function sanitizeErrorMessage(raw: string): string {
  return raw
    .replace(/[\u0000-\u001f\u007f]+/g, " ")
    .replace(/[^\s@]+@[^\s@]+/g, "<email>")
    .replace(/\b(?:https?|wss?|file|blob):\/\/\S+/gi, "<url>")
    .replace(/\b\d{1,3}(?:\.\d{1,3}){3}\b/g, "<ip>")
    // File paths (may hold a user name): C:\Users\x\..., /home/x/..., ~/...
    .replace(/\b[A-Za-z]:[\\/][^\s"'`]*/g, "<path>")
    .replace(/(?:~|\B)\/(?:[^\s/"'`]+\/)+[^\s"'`]*/g, "<path>")
    // Quoted fragments may hold data (JSON.parse snippets, names); keep only lower-case
    // identifiers such as property names ("reading 'foo'").
    .replace(/(["'`])([^"'`]*)\1/g, (m, q: string, inner: string) => (/^[a-z_$][\w$]{0,31}$/.test(inner) ? m : `${q}…${q}`))
    // Long numbers (ids, phone numbers).
    .replace(/\d{6,}/g, "<n>")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, ANALYTICS_LIMITS.maxErrorLength);
}

/** One clean event built field by field, or null when it is not valid. */
export function validateEvent(raw: unknown): AnalyticsEvent | null {
  if (!isObj(raw)) return null;
  switch (raw.e) {
    case "session": {
      const src = oneOf(SOURCES, raw.src);
      const dev = oneOf(DEVICES, raw.dev);
      const br = oneOf(BROWSERS, raw.br);
      const lang = typeof raw.lang === "string" && /^[a-z]{2}$/.test(raw.lang) ? raw.lang : null;
      return src && dev && br && lang ? { e: "session", src, dev, br, lang } : null;
    }
    case "funnel": {
      const step = oneOf(FUNNEL_STEPS, raw.step);
      return step ? { e: "funnel", step } : null;
    }
    case "gameStart":
    case "gameEnd": {
      const kind = oneOf(GAME_KINDS, raw.kind);
      const rs = oneOf(RULESETS, raw.rs);
      const mode = idStr(raw.mode);
      const map = idStr(raw.map);
      const diff = oneOf(DIFFICULTIES, raw.diff);
      if (!kind || !rs || !mode || !map || !diff) return null;
      if (raw.e === "gameStart") return { e: "gameStart", kind, rs, mode, map, diff };
      const result = oneOf(GAME_RESULTS, raw.result);
      const reason = oneOf(END_REASONS, raw.reason);
      const min = intIn(raw.min, 0, ANALYTICS_LIMITS.maxGameMinutes);
      const share = intIn(raw.share, 0, 100);
      if (!result || !reason || min === null || share === null) return null;
      return { e: "gameEnd", kind, rs, mode, map, diff, result, reason, min, share };
    }
    case "feature": {
      const f = idStr(raw.f);
      const n = intIn(raw.n, 1, ANALYTICS_LIMITS.maxFeatureCount);
      return f && n !== null ? { e: "feature", f, n } : null;
    }
    case "campaign": {
      const lvl = idStr(raw.lvl);
      const act = oneOf(CAMPAIGN_ACTIONS, raw.act);
      if (!lvl || !act) return null;
      if (raw.obj === undefined) return { e: "campaign", lvl, act };
      const obj = idStr(raw.obj);
      return obj ? { e: "campaign", lvl, act, obj } : null;
    }
    case "tech": {
      const dev = oneOf(DEVICES, raw.dev);
      const br = oneOf(BROWSERS, raw.br);
      if (!dev || !br) return null;
      const out: Extract<AnalyticsEvent, { e: "tech" }> = { e: "tech", dev, br };
      if (raw.load !== undefined) {
        const load = intIn(raw.load, 0, 600_000);
        if (load === null) return null;
        out.load = load;
      }
      if (raw.fps !== undefined) {
        const fps = intIn(raw.fps, 0, 1000);
        if (fps === null) return null;
        out.fps = fps;
      }
      if (raw.tick !== undefined) {
        const tick = intIn(raw.tick, 0, 10_000);
        if (tick === null) return null;
        out.tick = tick;
      }
      return out.load === undefined && out.fps === undefined && out.tick === undefined ? null : out;
    }
    case "error": {
      const dev = oneOf(DEVICES, raw.dev);
      const br = oneOf(BROWSERS, raw.br);
      const where = oneOf(ERROR_SOURCES, raw.where);
      const msg = typeof raw.msg === "string" ? sanitizeErrorMessage(raw.msg) : "";
      return dev && br && where && msg ? { e: "error", dev, br, where, msg } : null;
    }
    case "ab": {
      // Only ads experiment ids (`ads.<placement>.r<round>`): nothing else is ever reported.
      const id = idStr(raw.x);
      const x = id && parseAbExperimentId(id) ? id : null;
      const arm = oneOf(AB_ARMS, raw.arm);
      return x && arm ? { e: "ab", x, arm } : null;
    }
    default:
      return null;
  }
}

/**
 * Validate a parsed batch. Structural problems reject the whole batch; single invalid events
 * (and, without consent, every kind outside ANONYMOUS_KINDS) are dropped and counted.
 */
export function validateBatch(raw: unknown): BatchValidation {
  if (!isObj(raw) || !Array.isArray(raw.events) || typeof raw.consent !== "boolean") return { ok: false, error: "invalid_batch" };
  if (raw.v !== ANALYTICS_VERSION) return { ok: false, error: "unsupported_version" };
  if (raw.events.length > ANALYTICS_LIMITS.maxEvents) return { ok: false, error: "too_many_events" };
  const consent = raw.consent;
  if (!consent && (raw.pid !== undefined || raw.sid !== undefined)) return { ok: false, error: "id_without_consent" };
  if (consent && (typeof raw.pid !== "string" || !ANON_ID_RE.test(raw.pid))) return { ok: false, error: "missing_id" };
  const events: AnalyticsEvent[] = [];
  let dropped = 0;
  for (const r of raw.events) {
    const ev = validateEvent(r);
    if (!ev || (!consent && !ANONYMOUS_KINDS.has(ev.e))) dropped++;
    else events.push(ev);
  }
  const batch: AnalyticsBatch = consent ? { v: ANALYTICS_VERSION, consent, pid: raw.pid as string, events } : { v: ANALYTICS_VERSION, consent, events };
  return { ok: true, batch, dropped };
}

/** Responses of the public endpoints. */
export interface AnalyticsConfigResponse {
  enabled: boolean;
}
export interface AnalyticsCollectResponse {
  accepted: number;
  dropped: number;
}
